/**
 * RelationshipPicker - Hierarchical, searchable activity picker for relationships
 *
 * Features:
 * - Searchable by Activity ID or Name
 * - Hierarchical WBS structure with expand/collapse
 * - Shows Activity ID + Name together
 * - Performant for large schedules
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Search,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  Check,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const RelationshipPicker = ({
  wbsTree = [],
  activitiesByWbs = {},
  selectedActivityId,
  onSelect,
  excludeActivityId, // The current activity we're adding relationships for
  placeholder = "Search activities...",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState({});
  const inputRef = useRef(null);

  // Get all activities as a flat list
  const allActivities = useMemo(() => {
    const activities = [];
    Object.values(activitiesByWbs).forEach(wbsActivities => {
      wbsActivities.forEach(activity => {
        if (activity.id !== excludeActivityId) {
          activities.push(activity);
        }
      });
    });
    return activities;
  }, [activitiesByWbs, excludeActivityId]);

  // Find selected activity details
  const selectedActivity = useMemo(() => {
    return allActivities.find(a => a.id === selectedActivityId);
  }, [allActivities, selectedActivityId]);

  // Filter activities and WBS based on search
  const { filteredTree, matchingActivityIds } = useMemo(() => {
    if (!searchQuery.trim()) {
      return { filteredTree: wbsTree, matchingActivityIds: new Set() };
    }

    const query = searchQuery.toLowerCase().trim();
    const matchingIds = new Set();
    const matchingWbsIds = new Set();

    // Find matching activities
    allActivities.forEach(activity => {
      const matchesId = activity.activity_id?.toLowerCase().includes(query);
      const matchesName = activity.name?.toLowerCase().includes(query);
      if (matchesId || matchesName) {
        matchingIds.add(activity.id);
        matchingWbsIds.add(activity.wbs_id);
      }
    });

    // Find WBS nodes that contain matching activities
    const findWbsPath = (nodes, targetIds, path = new Set()) => {
      for (const node of nodes) {
        if (targetIds.has(node.id)) {
          path.add(node.id);
          // Add all parent ids
          let current = node;
          while (current.parent_id) {
            path.add(current.parent_id);
            current = wbsTree.find(n => n.id === current.parent_id) || { parent_id: null };
          }
        }
        if (node.children?.length > 0) {
          findWbsPath(node.children, targetIds, path);
        }
      }
      return path;
    };

    const relevantWbsIds = findWbsPath(wbsTree, matchingWbsIds);

    // Filter tree to only show relevant nodes
    const filterTree = (nodes) => {
      return nodes
        .filter(node => relevantWbsIds.has(node.id) || matchingWbsIds.has(node.id))
        .map(node => ({
          ...node,
          children: node.children ? filterTree(node.children) : []
        }));
    };

    return {
      filteredTree: filterTree(wbsTree),
      matchingActivityIds: matchingIds
    };
  }, [wbsTree, allActivities, searchQuery]);

  // Toggle WBS node expansion
  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  }, []);

  // Auto-expand all nodes when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      const allNodeIds = {};
      const collectIds = (nodes) => {
        nodes.forEach(node => {
          allNodeIds[node.id] = true;
          if (node.children?.length > 0) {
            collectIds(node.children);
          }
        });
      };
      collectIds(filteredTree);
      setExpandedNodes(allNodeIds);
    }
  }, [searchQuery, filteredTree]);

  // Handle activity selection
  const handleSelect = useCallback((activity) => {
    onSelect(activity.id);
    setOpen(false);
    setSearchQuery('');
  }, [onSelect]);

  // Render a single activity item
  const renderActivity = (activity, level) => {
    const isSelected = selectedActivityId === activity.id;
    const isMatching = matchingActivityIds.size > 0 && matchingActivityIds.has(activity.id);

    return (
      <div
        key={activity.id}
        className={cn(
          "flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-md transition-colors",
          "hover:bg-indigo-50",
          isSelected && "bg-indigo-100",
          isMatching && "ring-1 ring-indigo-300"
        )}
        style={{ paddingLeft: `${level * 16 + 12}px` }}
        onClick={() => handleSelect(activity)}
      >
        <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
        <span className={cn(
          "font-mono text-[11px] px-1.5 py-0.5 rounded flex-shrink-0",
          isSelected ? "bg-indigo-200 text-indigo-700" : "bg-slate-100 text-slate-600"
        )}>
          {activity.activity_id}
        </span>
        <span className="text-xs text-slate-700 truncate flex-1">
          {activity.name}
        </span>
        {isSelected && (
          <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
        )}
      </div>
    );
  };

  // Render WBS node with its activities
  const renderWbsNode = (node, level = 0) => {
    const isExpanded = expandedNodes[node.id] ?? level < 1; // Auto-expand first level
    const hasChildren = node.children?.length > 0;
    const activities = (activitiesByWbs[node.id] || []).filter(a =>
      a.id !== excludeActivityId &&
      (matchingActivityIds.size === 0 || matchingActivityIds.has(a.id))
    );
    const hasContent = hasChildren || activities.length > 0;

    if (!hasContent && searchQuery.trim()) {
      return null;
    }

    return (
      <div key={node.id} className="select-none">
        {/* WBS Header */}
        <div
          className={cn(
            "flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-md transition-colors",
            "hover:bg-slate-50"
          )}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => hasContent && toggleNode(node.id)}
        >
          {hasContent ? (
            isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
            )
          ) : (
            <span className="w-3.5" />
          )}
          {isExpanded ? (
            <FolderOpen className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
          ) : (
            <Folder className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
          )}
          <span className="font-mono text-[10px] text-slate-400 flex-shrink-0">
            {node.code}
          </span>
          <span className="text-xs font-medium text-slate-700 truncate">
            {node.name}
          </span>
          {activities.length > 0 && (
            <Badge variant="secondary" className="ml-auto text-[9px] px-1.5 py-0">
              {activities.length}
            </Badge>
          )}
        </div>

        {/* Children and Activities */}
        {isExpanded && (
          <div>
            {/* Child WBS nodes */}
            {node.children?.map(child => renderWbsNode(child, level + 1))}

            {/* Activities under this WBS */}
            {activities.map(activity => renderActivity(activity, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-start h-9 text-sm font-normal",
            !selectedActivity && "text-slate-400"
          )}
          disabled={disabled}
        >
          {selectedActivity ? (
            <span className="flex items-center gap-2 truncate">
              <span className="font-mono text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded flex-shrink-0">
                {selectedActivity.activity_id}
              </span>
              <span className="truncate">{selectedActivity.name}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Search className="h-3.5 w-3.5" />
              {placeholder}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[580px] p-0" align="start">
        {/* Search Input */}
        <div className="p-2 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              ref={inputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by ID or name..."
              className="h-8 pl-8 pr-8 text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-3.5 w-3.5 text-slate-400" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-[10px] text-slate-400 mt-1.5 px-1">
              {matchingActivityIds.size} activities found
            </p>
          )}
        </div>

        {/* Hierarchical List */}
        <ScrollArea className="h-[460px]">
          <div className="p-2">
            {filteredTree.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Search className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  {searchQuery ? 'No matching activities' : 'No activities available'}
                </p>
                {searchQuery && (
                  <p className="text-xs text-slate-400 mt-1">
                    Try a different search term
                  </p>
                )}
              </div>
            ) : (
              filteredTree.map(node => renderWbsNode(node))
            )}
          </div>
        </ScrollArea>

        {/* Footer hint */}
        <div className="p-2 border-t border-slate-100 bg-slate-50">
          <p className="text-[10px] text-slate-400 text-center">
            Click an activity to select • Use search to filter by ID or name
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RelationshipPicker;
